import { useCallback, useEffect, useRef, useState } from 'react';
import { useForegroundInterval } from './AppStateService';
import { captureActivityScrollAnchor } from './activityScrollRestoration';

const MAIN_JS_PATTERN = /\/static\/js\/main\.[^/]+\.js(?:$|[?#])/;
const MAIN_CSS_PATTERN = /\/static\/css\/main\.[^/]+\.css(?:$|[?#])/;

const assetPath = value => {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch (_) {
    return String(value);
  }
};

export function parseAssetManifestEntrypoints(manifest = {}) {
  return {
    js: assetPath(manifest.files?.['main.js']),
    css: assetPath(manifest.files?.['main.css']),
  };
}

export function getLoadedAssetEntrypoints(doc = document) {
  const script = Array.from(doc.scripts || []).find(item => MAIN_JS_PATTERN.test(item.src));
  const stylesheet = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'))
    .find(item => MAIN_CSS_PATTERN.test(item.href));
  return { js: assetPath(script?.src), css: assetPath(stylesheet?.href) };
}

export function hasAssetVersionChanged(current = {}, latest = {}) {
  if (!current.js || !latest.js) return false;
  return current.js !== latest.js || Boolean(current.css && latest.css && current.css !== latest.css);
}

export async function fetchLatestAssetEntrypoints(fetchImpl = fetch) {
  const response = await fetchImpl(`/asset-manifest.json?ts=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) throw new Error(`Version check failed (${response.status})`);
  return parseAssetManifestEntrypoints(await response.json());
}

export function buildVersionedReloadUrl(currentUrl, version) {
  const url = new URL(currentUrl);
  url.searchParams.set('__appv', version);
  return url.toString();
}

export function reloadToLatest(latest, locationRef = window.location, captureOptions = {}) {
  captureActivityScrollAnchor({ locationRef, ...captureOptions });
  const version = (latest?.js || 'latest').split('/').pop();
  locationRef.replace(buildVersionedReloadUrl(locationRef.href, version));
}

export function useAppUpdateAvailable() {
  const loadedAssets = useRef(getLoadedAssetEntrypoints());
  const [latestAssets, setLatestAssets] = useState(null);
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (checking.current || latestAssets) return;
    checking.current = true;
    try {
      const latest = await fetchLatestAssetEntrypoints();
      if (hasAssetVersionChanged(loadedAssets.current, latest)) setLatestAssets(latest);
    } catch (error) {
      console.warn('[AppVersion] deployment check failed:', error?.message || error);
    } finally {
      checking.current = false;
    }
  }, [latestAssets]);

  useEffect(() => {
    const initial = setTimeout(check, 5000);
    return () => clearTimeout(initial);
  }, [check]);

  useForegroundInterval(check, 10 * 60 * 1000, true);

  return latestAssets;
}
